import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { MapPin, Clock, ShieldCheck, ExternalLink, X } from "lucide-react";

const mapUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "-");

function Slot({ label, photoId, at, address, lat, lng, noGps, extraBadge, testid, onZoom }) {
  return (
    <div data-testid={testid} className="flex-1 min-w-0 bg-slate-50 rounded-lg p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{label}</p>
      {photoId ? (
        <button type="button" data-testid={`${testid}-photo`} onClick={() => onZoom(fileUrl(photoId))} className="block w-full cursor-zoom-in">
          <img src={fileUrl(photoId)} alt={label} className="w-full h-32 rounded-lg object-cover border border-slate-200" />
        </button>
      ) : (
        <div className="w-full h-32 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">Belum ada</div>
      )}
      <p className="mt-2 text-xs text-slate-600 flex items-center gap-1"><Clock size={11} /> {fmtTime(at)}</p>
      <div className="mt-1 flex items-start gap-1 text-xs text-slate-600">
        <MapPin size={11} className="mt-0.5 flex-shrink-0" />
        {noGps ? (
          <span className="text-amber-700 font-semibold">Tanpa GPS (izin lokasi ditolak)</span>
        ) : lat && lng ? (
          <a data-testid={`${testid}-map`} href={mapUrl(lat, lng)} target="_blank" rel="noreferrer" className="text-blue-600 font-semibold hover:underline flex items-start gap-1 min-w-0">
            <span className="line-clamp-2">{address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`}</span>
            <ExternalLink size={11} className="flex-shrink-0 mt-0.5" />
          </a>
        ) : (
          <span className="text-slate-400">-</span>
        )}
      </div>
      {extraBadge}
    </div>
  );
}

export default function AttendanceVerify() {
  const [date, setDate] = useState(() => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10));
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const openZoom = (src) => { setZoomSrc(src); setZoomed(false); };

  useEffect(() => {
    setRows(null);
    setError(false);
    api.get(`/attendance?date=${date}`)
      .then(({ data }) => setRows(data))
      .catch(() => setError(true));
  }, [date]);

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Anti-Kecurangan</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Verifikasi Absen</h1>
        <p className="text-sm text-slate-600 mt-1">Cek foto selfie & titik lokasi GPS setiap karyawan. Klik alamat untuk membuka peta.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tanggal</label>
        <input
          data-testid="verify-date-filter"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm"
        />
        <span className="text-xs text-slate-500">{rows ? `${rows.length} karyawan absen` : "..."}</span>
      </div>

      <div data-testid="verify-list" className="space-y-3">
        {error && (
          <div data-testid="verify-error" className="bg-red-50 rounded-xl border border-red-200 p-6 text-center text-sm text-red-700">
            Gagal memuat data absensi. Periksa koneksi lalu ganti tanggal untuk mencoba lagi.
          </div>
        )}
        {rows === null && !error && <p className="text-sm text-slate-500">Memuat...</p>}
        {rows !== null && rows.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-sm text-slate-500">
            Tidak ada absensi pada tanggal ini.
          </div>
        )}
        {(rows || []).map((r) => (
          <div key={r.id} data-testid={`verify-item-${r.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0"><ShieldCheck size={17} /></div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 truncate" style={{ fontFamily: "Manrope" }}>{r.user_name}</p>
                  <p className="text-xs text-slate-500">{r.date} · {r.role === "cabang" ? "Cabang" : r.role === "gudang" ? "Gudang" : r.role}</p>
                </div>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {r.is_late && <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[10px] font-bold uppercase tracking-wider">Telat {r.late_minutes} mnt</span>}
                {r.left_early && <span className="px-2.5 py-1 rounded-full bg-orange-100 text-orange-800 text-[10px] font-bold uppercase tracking-wider">Pulang awal {r.early_minutes} mnt</span>}
                {(r.clock_in_no_gps || r.clock_out_no_gps) && <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">Tanpa GPS</span>}
                {!r.is_late && r.clock_in_at && <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">Masuk Tepat Waktu</span>}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Slot testid={`verify-in-${r.id}`} label="Masuk" photoId={r.clock_in_photo_id} at={r.clock_in_at} address={r.clock_in_address} lat={r.clock_in_lat} lng={r.clock_in_lng} noGps={r.clock_in_no_gps} onZoom={openZoom} />
              <Slot testid={`verify-out-${r.id}`} label="Keluar" photoId={r.clock_out_photo_id} at={r.clock_out_at} address={r.clock_out_address} lat={r.clock_out_lat} lng={r.clock_out_lng} noGps={r.clock_out_no_gps} onZoom={openZoom} />
            </div>
          </div>
        ))}
      </div>

      {zoomSrc && (
        <div data-testid="photo-lightbox" onClick={() => setZoomSrc(null)} className="fixed inset-0 z-50 bg-black/90 overflow-auto flex items-center justify-center p-4">
          <button data-testid="photo-lightbox-close" onClick={() => setZoomSrc(null)} className="fixed top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center"><X size={20} /></button>
          <img
            src={zoomSrc}
            alt="Foto absen"
            onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
            className={zoomed ? "max-w-none w-[180%] cursor-zoom-out" : "max-h-[85vh] max-w-[95vw] object-contain rounded-lg cursor-zoom-in"}
          />
          <p className="fixed bottom-4 left-0 right-0 text-center text-white/70 text-xs">Ketuk foto untuk zoom wajah · ketuk area gelap untuk tutup</p>
        </div>
      )}
    </div>
  );
}
